// javascript
import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BusinessIcon from '@mui/icons-material/Business';
import EventIcon from '@mui/icons-material/Event';
import ForwardIcon from '@mui/icons-material/Forward';
import DescriptionIcon from '@mui/icons-material/Description';
import BotonWhatsapp from '../components/BotonWhatsapp';
import cambioEstadoService from '../services/cambioEstadoService';
import EventoService from '../services/eventoService';
import NotificacionService from '../services/notificacionService';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import ModalConfirmacionCambioEstado from '../components/ModalConfirmacionCambioEstado';
import TarjetaCitacion from '../components/recepcion/CitacionEstudio';

function GestorEstadoJuntaMedica({ tramite, onEstadoCambiado }) {
    const [cargando, setCargando] = useState(false);
    const [juntaInfo, setJuntaInfo] = useState(null);
    const [cargandoJunta, setCargandoJunta] = useState(true);
    const [mostrarModalConfirmacion, setMostrarModalConfirmacion] = useState(false);

    useEffect(() => {
        const cargarJuntaMedica = async () => {
            if (!tramite?.idTramite) return;
            setCargandoJunta(true);
            try {
                const response = await EventoService.getEventosPorTramite(tramite.idTramite);
                const eventoJunta = response.data.find(e => e.tipoEvento === 'Junta Médica');
                setJuntaInfo(eventoJunta || null);
            } catch (error) {
                console.error("Error al cargar detalles de la Junta Médica:", error);
                toast.error("No se pudieron cargar los detalles de la Junta Médica.");
            } finally {
                setCargandoJunta(false);
            }
        };
        cargarJuntaMedica();
    }, [tramite.idTramite]);

    const handleRegistrarJuntaRealizada = async () => {
        setCargando(true);
        try {
            const cambioEstadoData = {
                idEstado: 7, // Cambiamos al estado 7: Espera de 1° Dictamen
                comentario: `La Junta Médica se ha marcado como realizada. Se espera el dictamen de ANSES.`
            };

            await cambioEstadoService.cambiarEstado(tramite.idTramite, cambioEstadoData);

            toast.success('Junta Médica registrada. El trámite avanzó a "Espera de Dictamen".');
            setMostrarModalConfirmacion(false);

            if (onEstadoCambiado) {
                await onEstadoCambiado();
            }
        } catch (error) {
            console.error('Error al registrar la junta realizada:', error);
            toast.error('No se pudo actualizar el estado del trámite.');
        } finally {
            setCargando(false);
        }
    };

    const formatearFecha = (fechaString) => {
        if (!fechaString) return 'No disponible';
        const fecha = new Date(fechaString);
        const fechaLocal = new Date(fecha.valueOf() + fecha.getTimezoneOffset() * 60000);
        return fechaLocal.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    };

    const renderDetallesJunta = () => {
        if (cargandoJunta) {
            return <div className="alert alert-info">Cargando detalles de la Junta Médica...</div>;
        }
        if (!juntaInfo) {
            return <div className="alert alert-warning">No se encontraron detalles para la Junta Médica.</div>;
        }

        const descripcion = juntaInfo.descripcion || '';
        const expedienteMatch = descripcion.match(/N° Expediente: (.*?)(?:\n|$)/);
        const sedeMatch = descripcion.match(/Sede: (.*?)(?:\n|$)/);
        const observacionesMatch = descripcion.match(/Observación: (.*?)(?:$)/);

        const numeroExpediente = expedienteMatch ? expedienteMatch[1] : 'No especificado';
        const sede = sedeMatch ? sedeMatch[1] : 'No especificada';
        const observacionesTurno = observacionesMatch ? observacionesMatch[1] : 'No especificadas';

        return (
            <div className="mt-1 p-3 bg-light rounded-3 border">
                <div className="row g-3">
                    <div className="col-md-6 d-flex align-items-center">
                        <DescriptionIcon className="text-muted me-2" />
                        <div>
                            <small className="text-muted d-block">N° Expediente</small>
                            <strong className="text-dark">{numeroExpediente}</strong>
                        </div>
                    </div>
                    <div className="col-md-6 d-flex align-items-center">
                        <CalendarMonthIcon className="text-muted me-2" />
                        <div>
                            <small className="text-muted d-block">Fecha</small>
                            <strong className="text-dark">{formatearFecha(juntaInfo.fecha)}</strong>
                        </div>
                    </div>
                    <div className="col-md-6 d-flex align-items-center">
                        <AccessTimeIcon className="text-muted me-2" />
                        <div>
                            <small className="text-muted d-block">Hora</small>
                            <strong className="text-dark">{juntaInfo.horaInicio || 'N/A'}</strong>
                        </div>
                    </div>
                    <div className="col-md-6 d-flex align-items-center">
                        <BusinessIcon className="text-muted me-2" />
                        <div>
                            <small className="text-muted d-block">Sede</small>
                            <strong className="text-dark">{sede}</strong>
                        </div>
                    </div>
                    {observacionesTurno && (
                        <div className="mt-3 p-3 bg-white rounded-3 border">
                            <small className="text-muted d-block mb-1">Observación:</small>
                            <small className="text-dark" style={{ whiteSpace: 'pre-line' }}>
                                {observacionesTurno}
                            </small>
                        </div>
                    )}
                </div>

                <div className="mt-3 d-flex gap-2">
                    <BotonWhatsapp
                        idEvento={juntaInfo?.idEvento}
                        numeroTelefono={tramite.cliente?.telefonoPropio || tramite.cliente?.telefono}
                        nombreCliente={`${tramite.cliente?.nombre || ''} ${tramite.cliente?.apellido || ''}`.trim()}
                        contactosAdicionales={tramite.cliente?.contactos}
                        fechaTurno={juntaInfo?.fecha}
                        horaTurno={juntaInfo?.horaInicio || juntaInfo?.hora}
                        lugar={sede}
                        tipoEvento="Turno Junta Médica"
                        tramiteId={tramite?.idTramite}
                        className="btn-sm"
                    />
                </div>

                {/* Citación al estudio antes de la junta (V254): el recordatorio usa su hora. */}
                <TarjetaCitacion evento={juntaInfo} tipo="JUNTA_MEDICA" cliente={tramite.cliente} />
            </div>
        );
    };

    return (
        <div className="card shadow-lg border-0 rounded-4 p-4 mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="fw-bold text-info mb-0">Turno Junta Médica</h4>
                <span className="badge bg-info rounded-pill px-3 py-2">
                    <EventIcon fontSize="small" className="me-1" />
                    Pendiente de Realización
                </span>
            </div>

            <div className="alert alert-info border-0 rounded-3">
                <strong>Siguiente Paso:</strong>
                <p className="mb-0 mt-1">Esperar a la fecha de la junta. Una vez realizada, registrar el resultado.</p>
            </div>

            {renderDetallesJunta()}

            <div className="d-flex justify-content-center flex-wrap gap-3 mt-4 pt-3 border-top">
                <button
                    className="btn btn-success d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                    onClick={() => setMostrarModalConfirmacion(true)}
                    disabled={cargando || cargandoJunta}
                >
                    <ForwardIcon fontSize="small" />
                    Registrar Junta Realizada
                </button>
            </div>

            {/* Modal de Confirmación */}
            <ModalConfirmacionCambioEstado
                isOpen={mostrarModalConfirmacion}
                onClose={() => setMostrarModalConfirmacion(false)}
                onConfirm={handleRegistrarJuntaRealizada}
                titulo="Confirmar Junta Médica Realizada"
                mensaje="¿Confirma que la Junta Médica se ha realizado y desea avanzar al siguiente estado?"
                siguienteEstado="Espera de 1° Dictamen"
                cargando={cargando}
                textoBotonConfirmar="Confirmar"
            />
        </div>
    );
}

export default GestorEstadoJuntaMedica;
